/**
 * Classifies NATS UAS restriction features. The dataset carries only a name
 * and the AIP description text, so the category and vertical limits are read
 * from those. The type labels are chosen to match rows in the restrictions
 * table, so the permission route follows from the category.
 */

export type ZoneCategory = "aerodrome" | "security" | "prison" | "danger" | "other";

export interface ZoneCategoryInfo {
  /** Legend and layer-toggle label. */
  label: string;
  /** Type shown in results; matched against the restrictions table. */
  type: string;
  colour: string;
  /** Stroke dash pattern, so categories differ by more than colour. */
  dashArray?: string;
}

/** Listed in legend order: most commonly encountered first. */
export const zoneCategories: Readonly<Record<ZoneCategory, ZoneCategoryInfo>> = {
  aerodrome: {
    label: "Airports and airfields",
    type: "Aerodrome FRZ / RPZ",
    colour: "#0b5ed7",
  },
  security: {
    label: "MoD and prohibited sites",
    type: "MoD Prohibited Place",
    colour: "#b02a37",
    dashArray: "6 3",
  },
  prison: {
    label: "Prisons",
    type: "Prison",
    colour: "#6f42c1",
    dashArray: "2 3",
  },
  danger: {
    label: "Danger areas (ranges)",
    type: "Danger area",
    colour: "#c25700",
    dashArray: "10 4 2 4",
  },
  other: {
    label: "Other restricted areas",
    type: "Restricted area",
    colour: "#495057",
    dashArray: "1 4",
  },
};

export interface ZoneLimits {
  upper: string;
  lower: string;
}

export function zoneCategory(name: string, description = ""): ZoneCategory {
  if (name.startsWith("EGP") || /\bNSA\b/.test(name)) return "security";
  if (name.startsWith("EGD")) return "danger";
  if (/\b(HMP|HMYOI|YOI|PRISON)\b/i.test(name)) return "prison";
  if (/\bRWY\b|\bFRZ\b/.test(`${name} ${description}`)) return "aerodrome";
  return "other";
}

/** Reads "Upper limit: … Lower limit: …" from the AIP description, if present. */
export function zoneLimits(description = ""): ZoneLimits | null {
  const match = /Upper limit:\s*(.+?)\s+Lower limit:\s*(.+?)(?:\s{2,}|\s+Class:|$)/.exec(
    description,
  );
  if (!match?.[1] || !match[2]) return null;
  return { upper: expandLimit(match[1]), lower: expandLimit(match[2]) };
}

function expandLimit(limit: string): string {
  if (limit === "SFC") return "Surface";
  if (limit === "UNL") return "Unlimited";
  return limit.replace(/\bFT\b/, "ft");
}
